<template>
    <div class="work-time-panel" v-loading="state.loading">
        <div class="chart-section">
            <div class="chart-header">
                <div class="section-title">{{ t('deviceWorkTime.trendMonths', { n: state.months }) }}</div>
                <div class="range-picker">
                    <span class="label">{{ t('deviceWorkTime.trendRange') }}</span>
                    <el-radio-group v-model="state.months" size="small" @change="onMonthsChange">
                        <el-radio-button :label="6">6{{ t('deviceWorkTime.monthsSuffix') }}</el-radio-button>
                        <el-radio-button :label="12">12{{ t('deviceWorkTime.monthsSuffix') }}</el-radio-button>
                        <el-radio-button :label="24">24{{ t('deviceWorkTime.monthsSuffix') }}</el-radio-button>
                    </el-radio-group>
                </div>
            </div>
            <div ref="chartRef" class="trend-chart"></div>
        </div>

        <div class="month-toolbar">
            <span class="label">{{ t('deviceWorkTime.viewMonth') }}</span>
            <el-date-picker
                v-model="state.selectedMonth"
                type="month"
                value-format="YYYY-MM"
                :placeholder="t('common.selectMonth')"
                @change="loadMonthData"
            />
        </div>

        <div class="summary-row" v-if="state.summary.total_time != null">
            <div class="summary-item">
                <div class="num">{{ state.summary.work_days ?? 0 }}</div>
                <div class="txt">{{ t('deviceWorkTime.workDays') }}</div>
            </div>
            <div class="summary-item">
                <div class="num">{{ state.summary.operator_count ?? 0 }}</div>
                <div class="txt">{{ t('deviceWorkTime.operatorCount') }}</div>
            </div>
            <div class="summary-item">
                <div class="num">{{ state.summary.standard_time ?? 0 }}</div>
                <div class="txt">{{ t('deviceWorkTime.standardTime') }}</div>
            </div>
            <div class="summary-item">
                <div class="num">{{ state.summary.ot_time ?? 0 }}</div>
                <div class="txt">{{ t('deviceWorkTime.otTime') }}</div>
            </div>
            <div class="summary-item">
                <div class="num">{{ state.summary.total_time ?? 0 }}</div>
                <div class="txt">{{ t('deviceWorkTime.totalTime') }}</div>
            </div>
        </div>

        <div class="section-title">{{ t('deviceWorkTime.operatorBreakdown') }}</div>
        <el-table :data="state.operators" border stripe max-height="360" :empty-text="t('deviceWorkTime.emptyMonth')">
            <el-table-column type="index" :label="t('table.index')" width="60" />
            <el-table-column prop="real_name" :label="t('deviceWorkTime.machinist')" min-width="100" />
            <el-table-column prop="mobile" :label="t('field.mobile')" min-width="120" />
            <el-table-column prop="work_days" :label="t('deviceWorkTime.workDays')" width="90" />
            <el-table-column prop="standard_time" :label="t('deviceWorkTime.clockOutShort')" width="80" />
            <el-table-column prop="ot_time" :label="t('deviceWorkTime.otShort')" width="80" />
            <el-table-column prop="zhi_time" :label="t('deviceWorkTime.zhiShort')" width="80" />
            <el-table-column prop="tongxiao_time" :label="t('deviceWorkTime.tongxiaoShort')" width="80" />
            <el-table-column prop="total_time" :label="t('deviceWorkTime.totalShort')" width="80" />
        </el-table>

        <div class="tip">{{ t('deviceWorkTime.tip') }}</div>
    </div>
</template>

<script setup>
import { reactive, ref, onMounted, onUnmounted, watch, nextTick } from "vue";
import {
    workTimeStatsDeviceApi,
    workTimeStatsTrendApi,
    workTimeStatsOperatorsApi,
} from "@/api/system/device";
import {
    renderDeviceWorkTimeTrend,
    resizeDeviceWorkTimeTrend,
    disposeDeviceWorkTimeTrend,
} from "./deviceWorkTimeTrendChart.js";
import { usePageI18n } from '@/composables/usePageI18n.js';

const applyLabels = () => {};
const { t } = usePageI18n(applyLabels);

const props = defineProps({
    deviceId: {
        type: [String, Number],
        required: true,
    },
    months: {
        type: Number,
        default: 12,
    },
});

const trendMonthOptions = [6, 12, 24];

const chartRef = ref();
const state = reactive({
    loading: false,
    months: trendMonthOptions.includes(props.months) ? props.months : 12,
    selectedMonth: "",
    summary: {},
    operators: [],
    trendLabels: [],
    trendValues: [],
});

const currentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const renderChart = () => {
    nextTick(() => {
        renderDeviceWorkTimeTrend(
            chartRef.value,
            state.trendLabels,
            state.trendValues
        );
    });
};

const loadTrend = async () => {
    if (!props.deviceId) return;
    const res = await workTimeStatsTrendApi(
        JSON.stringify({
            device_list_id: Number(props.deviceId),
            months: state.months,
        })
    );
    const list = res?.data ?? [];
    state.trendLabels = list.map((i) => i.month);
    state.trendValues = list.map((i) => Number(i.total_time ?? 0));
    renderChart();
};

const loadMonthData = async () => {
    if (!props.deviceId || !state.selectedMonth) return;
    state.loading = true;
    try {
        const [summaryRes, operatorsRes] = await Promise.all([
            workTimeStatsDeviceApi(
                JSON.stringify({
                    device_list_id: Number(props.deviceId),
                    time: state.selectedMonth,
                })
            ),
            workTimeStatsOperatorsApi(
                JSON.stringify({
                    device_list_id: Number(props.deviceId),
                    time: state.selectedMonth,
                })
            ),
        ]);
        state.summary = summaryRes?.data ?? {};
        state.operators = operatorsRes?.data ?? [];
    } finally {
        state.loading = false;
    }
};

const loadAll = async () => {
    if (!props.deviceId) return;
    state.loading = true;
    try {
        await loadTrend();
        await loadMonthData();
    } finally {
        state.loading = false;
    }
};

const onMonthsChange = async () => {
    if (!props.deviceId) return;
    state.loading = true;
    try {
        await loadTrend();
    } finally {
        state.loading = false;
    }
};

watch(
    () => props.deviceId,
    () => {
        state.selectedMonth = currentMonth();
        loadAll();
    }
);

onMounted(() => {
    applyLabels();
    state.selectedMonth = currentMonth();
    loadAll();
    window.addEventListener("resize", resizeDeviceWorkTimeTrend);
});

onUnmounted(() => {
    window.removeEventListener("resize", resizeDeviceWorkTimeTrend);
    disposeDeviceWorkTimeTrend();
});

defineExpose({ reload: loadAll });
</script>

<style lang="less" scoped>
.work-time-panel {
    min-height: 200px;
}

.section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;
}

.chart-section {
    margin-bottom: 20px;
}

.chart-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;

    .section-title {
        margin-bottom: 0;
    }
}

.range-picker {
    display: flex;
    align-items: center;
    gap: 10px;

    .label {
        font-size: 13px;
        color: #606266;
    }
}

.trend-chart {
    width: 100%;
    height: 300px;
}

.month-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;

    .label {
        font-size: 14px;
        color: #606266;
    }
}

.summary-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 20px;

    .summary-item {
        flex: 1;
        min-width: 100px;
        padding: 12px 16px;
        background: #f5f8ff;
        border-radius: 6px;
        text-align: center;

        .num {
            font-size: 22px;
            font-weight: 600;
            color: #1e6aed;
            line-height: 1.2;
        }

        .txt {
            margin-top: 4px;
            font-size: 12px;
            color: #909399;
        }
    }
}

.tip {
    margin-top: 12px;
    font-size: 12px;
    color: #909399;
}
</style>
